import { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { detectTranscriptGaps, type TranscriptGap } from "@/lib/knowledge/transcriptGaps";
import { Conversation } from "@/types/conversation/conversation";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ConversationKnowledgeGapsProps {
  conversation: Conversation;
}

/**
 * Detects moments where the caller asked a question and the genie's reply
 * looked like a hedge or non-answer — surfaces each one as a knowledge
 * gap with a one-click "Add to knowledge base" CTA.
 *
 * The detector itself lives in `src/lib/knowledge/transcriptGaps.ts` so the
 * Knowledge Base's cross-conversation gap panel runs the exact same heuristic.
 *
 * This is the page's headline new value: every gap is a piece of training
 * data the owner can feed back into the genie in one click.
 */
export function ConversationKnowledgeGaps({ conversation }: ConversationKnowledgeGapsProps) {
  const transcript = conversation.details?.transcript;
  const agentId = conversation.agent_id;

  const gaps = useMemo(() => detectTranscriptGaps(transcript), [transcript]);

  // Only render the card when there's something to say. The empty state
  // would just dilute the page — a confident genie shouldn't show this
  // panel at all.
  if (gaps.length === 0) return null;

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 md:px-6 py-4 border-b border-[#4E9CFF]/10"
          style={{
            background:
              "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <GenieIcon
              name="book-open"
              size={26}
              color="#4E9CFF"
              secondaryColor="#FF4DB8"
              gradientEnabled
              label="Knowledge gaps"
            />
            <div>
              <h3 className="text-base font-bold text-gray-900">Knowledge gaps detected</h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Moments where the genie hesitated. Add an answer so it nails the next one.
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 md:px-6 py-5 space-y-3">
          {gaps.map((gap) => (
            <GapRow key={gap.index} gap={gap} agentId={agentId} />
          ))}
        </div>
      </div>
    </div>
  );
}

function GapRow({ gap, agentId }: { gap: TranscriptGap; agentId: string }) {
  return (
    <div className="rounded-xl border border-gray-200 hover:border-[#4E9CFF]/40 transition-colors p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[11px] uppercase tracking-wide font-semibold text-[#FF4DB8]">
              Caller asked
            </span>
            {gap.timestamp && (
              <span className="text-[11px] font-mono text-gray-500">{gap.timestamp}</span>
            )}
          </div>
          <p className="text-sm text-gray-900 font-medium leading-relaxed">
            &ldquo;{gap.question}&rdquo;
          </p>
          <p className="text-xs text-gray-500 mt-2 leading-relaxed">
            <span className="font-semibold text-gray-600">Genie:</span>{" "}
            <span className="italic">&ldquo;{truncate(gap.agentReply, 140)}&rdquo;</span>
          </p>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-end">
        <Link
          to={`/genie-hub/knowledge-base/${agentId}`}
          className="inline-flex items-center gap-1.5 text-xs hover:gap-2 transition-all"
        >
          <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent font-semibold">
            Add to knowledge base
          </span>
          <ArrowRight className="w-3 h-3 text-[#FF4DB8]" />
        </Link>
      </div>
    </div>
  );
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1).trimEnd() + "…";
}
